import { ScrollText, Upload } from "lucide-react"
import { Link, useLocation } from "react-router-dom"
import { Button } from "@/components/ui/Button"
import { PATH_ROOT } from "src/routes/paths"

/**
 * Empty state for a deal that has no contracts attached yet.
 *
 * Route is authoritative for which surface we're on — NOT the global
 * useIsDemo() localStorage flag, which persists after a demo tour and would
 * otherwise point the prod empty state at a demo route (or vice-versa). The
 * /demo path prefix is the unambiguous, weakest-assumption signal — matches
 * DealPage.tsx / NewDealPage.tsx / ContractIntelligencePage.tsx. Direct links
 * work too.
 *
 * On the REAL path the primary "Upload a contract" CTA routes to the upload
 * seam NewDealPage owns (/new-deal?method=upload — S3 presign → engine push →
 * real deal), giving a new deal a clear path forward. The demo surface keeps
 * the original inert "Add Contract" affordance — its behavior is unchanged.
 */
const EmptyContractsStateInner = () => {
  const location = useLocation()
  const isDemo = location.pathname.startsWith("/demo")

  return (
    <div className="p-6 flex flex-col items-center justify-center h-full min-h-[400px] text-center">
      <div className="p-4 rounded-full bg-muted/50 mb-4">
        <ScrollText className="size-8 text-muted-foreground" />
      </div>
      <h3 className="text-foreground font-semibold mb-2">No Contracts Yet</h3>
      <p className="text-muted-foreground text-sm max-w-sm">
        Add contracts to this deal to track agreements, terms, and signatures.
      </p>
      {isDemo ? (
        <Button variant="secondary" className="mt-4">
          <ScrollText className="size-4 mr-2" />
          Add Contract
        </Button>
      ) : (
        <Button
          variant="secondary"
          className="mt-4"
          render={<Link to={`${PATH_ROOT.main.newDeal}?method=upload`} />}
        >
          <Upload className="size-4 mr-2" />
          Upload a contract
        </Button>
      )}
    </div>
  )
}

// Exported as a rendered element (not a component) so callers inline it with
// no props — the surface gate lives inside EmptyContractsStateInner.
export const EmptyContractsState = <EmptyContractsStateInner />
